import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type SoftphoneStatus = 'offline' | 'ready' | 'connecting' | 'on-call' | 'ringing' | 'consulting';

export interface LastCallInfo {
  callId: string | null;
  customerName: string | null;
  callerNumber: string;
  duration: number;
  direction: 'inbound' | 'outbound' | null;
  ivrSelection: string | null;
  callSid: string | null;
}

interface SoftphoneState {
  status: SoftphoneStatus;
  activeCall: Call | null;
  incomingCall: Call | null;
  callDuration: number;
  callerNumber: string;
  callDirection: 'inbound' | 'outbound' | null;
  isMuted: boolean;
  isSpeakerMuted: boolean;
  isOnHold: boolean;
  consultCallSid: string | null;
  conferenceName: string | null;
  holdConferenceName: string | null;
  consultConferenceName: string | null;
  agentConsultCallSid: string | null;
  consultingNumber: string | null;
  customerCallSid: string | null;
  lastCallInfo: LastCallInfo | null;
}

export function useSoftphone() {
  const { user, authUser } = useAuth();
  const deviceRef = useRef<Device | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callStartRef = useRef<number>(0);

  const activeCallSidRef = useRef<string | null>(null);
  const customerCallSidRef = useRef<string | null>(null);
  const pendingDequeueRef = useRef(false);
  const pendingWarmConsultRef = useRef(false);
  const pendingWarmReconnectRef = useRef(false);
  const stateRef = useRef<SoftphoneState | null>(null);
  const initCalledRef = useRef(false);
  const callTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callConnectedRef = useRef(false);
  // Tracks whether we've inserted a switchboard_calls row for the current outbound call
  const outboundCallLoggedRef = useRef(false);
  const outboundToNumberRef = useRef<string | null>(null);
  const outboundStartedAtRef = useRef<string | null>(null);

  const setPendingDequeue = useCallback((val: boolean) => {
    pendingDequeueRef.current = val;
  }, []);

  const [state, setState] = useState<SoftphoneState>({
    status: 'offline',
    activeCall: null,
    incomingCall: null,
    callDuration: 0,
    callerNumber: '',
    callDirection: null,
    isMuted: false,
    isSpeakerMuted: false,
    isOnHold: false,
    consultCallSid: null,
    conferenceName: null,
    holdConferenceName: null,
    consultConferenceName: null,
    agentConsultCallSid: null,
    consultingNumber: null,
    customerCallSid: null,
    lastCallInfo: null,
  });

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const normalizePhoneForCompare = useCallback((raw: string) => {
    const cleaned = (raw || '').replace(/^client:/, '').replace(/\s+/g, '');
    if (!cleaned) return '';
    if (cleaned.startsWith('+')) return cleaned;
    if (cleaned.startsWith('00')) return `+${cleaned.slice(2)}`;
    return `+45${cleaned}`;
  }, []);

  // Fetch Twilio token and initialize device
  const initialize = useCallback(async () => {
    if (!authUser) return;

    try {
      setState(s => ({ ...s, status: 'connecting' }));

      // Force a token refresh to avoid stale sessions
      const { data: { user: refreshedUser }, error: refreshErr } = await supabase.auth.getUser();
      if (refreshErr || !refreshedUser) throw new Error('No active session');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/twilio-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!res.ok) throw new Error('Failed to get token');
      const { token } = await res.json();

      // Destroy existing device
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }

      // Safari/iOS prefers PCMU; detect Safari to adjust codec order
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const codecs = isSafari
        ? [Call.Codec.PCMU, Call.Codec.Opus]
        : [Call.Codec.Opus, Call.Codec.PCMU];

      const device = new Device(token, {
        codecPreferences: codecs,
        logLevel: isSafari ? 2 : 1, // More verbose logging on Safari for debugging
        allowIncomingWhileBusy: true,
        edge: ['dublin', 'ashburn'], // Closest edges for DK
      });

      device.on('registered', () => {
        setState(s => ({ ...s, status: 'ready' }));
        // Agent status is now managed by useAutoAgentStatus hook
      });

      device.on('error', (err: any) => {
        console.error('Twilio Device error:', err);
        const code = err?.code || err?.causes?.[0]?.code;
        if (code === 31005 || code === 31009) {
          toast.error('Telefonforbindelsen blev afbrudt. Tjek din internetforbindelse — systemet forsøger at genoprette automatisk.', { duration: 8000 });
        } else if (code === 31204 || code === 31205) {
          toast.error('Telefonens adgangstoken er udløbet. Genstart telefonen for at oprette forbindelse igen.');
        } else if (code === 31003) {
          toast.error('Telefonen er ikke registreret. Prøv at lukke og åbne telefonen igen.');
        } else {
          toast.error('Telefonfejl: ' + (err.message || 'Ukendt fejl'));
        }
      });

      device.on('incoming', (call: Call) => {
        // Prefer originalCaller parameter (set by our webhook) over From (which is the Twilio number for ring-group calls)
        const originalCaller = (call as any).customParameters?.get?.('originalCaller') || '';
        const from = originalCaller || call.parameters?.From || 'Ukendt';
        const currentState = stateRef.current;
        const isWarmReconnectFlow = pendingWarmReconnectRef.current;
        const isConsultingFlow = currentState?.status === 'consulting' || pendingWarmConsultRef.current;

        // Auto-accept if this incoming call was triggered by a dequeue action
        if (pendingDequeueRef.current) {
          pendingDequeueRef.current = false;
          call.accept();
          // Capture browser-leg CallSid
          const legSid = call.parameters?.CallSid || null;
          if (legSid) activeCallSidRef.current = legSid;
          setState(s => ({
            ...s,
            status: 'on-call',
            activeCall: call,
            incomingCall: null,
            callerNumber: from,
            callDirection: 'inbound',
          }));
          startTimer();
          // Look up customer's original CallSid from switchboard_calls
          lookupCustomerCallSid(from);

          call.on('accept', () => {
            if (!activeCallSidRef.current && call.parameters?.CallSid) {
              activeCallSidRef.current = call.parameters.CallSid;
            }
          });

          call.on('disconnect', () => {
            handleCallEnd();
          });
          return;
        }

        // Warm transfer fallback reconnect leg: auto-accept and restore normal on-call state
        if (isWarmReconnectFlow) {
          pendingWarmReconnectRef.current = false;
          pendingWarmConsultRef.current = false;
          call.accept();
          const legSid = call.parameters?.CallSid || null;
          if (legSid) activeCallSidRef.current = legSid;

          setState(s => ({
            ...s,
            status: 'on-call',
            activeCall: call,
            incomingCall: null,
            callerNumber: s.callerNumber || from,
            callDirection: 'inbound',
          }));
          startTimer();
          lookupCustomerCallSid(from);

          call.on('accept', () => {
            if (!activeCallSidRef.current && call.parameters?.CallSid) {
              activeCallSidRef.current = call.parameters.CallSid;
            }
          });

          call.on('disconnect', () => {
            handleCallEnd();
          });

          return;
        }

        // Warm transfer consult leg: treat as continuation of consulting flow, not as a new call
        if (isConsultingFlow) {
          pendingWarmConsultRef.current = false;
          call.accept();
          const legSid = call.parameters?.CallSid || null;
          if (legSid) activeCallSidRef.current = legSid;

          setState(s => ({
            ...s,
            status: 'consulting',
            activeCall: call,
            incomingCall: null,
            callerNumber: s.callerNumber || from,
          }));

          call.on('accept', () => {
            if (!activeCallSidRef.current && call.parameters?.CallSid) {
              activeCallSidRef.current = call.parameters.CallSid;
            }
          });

          call.on('disconnect', () => {
            if (stateRef.current?.status === 'consulting') {
              setState(s => ({ ...s, activeCall: null }));
              return;
            }
            handleCallEnd();
          });

          return;
        }

        setState(s => ({
          ...s,
          status: 'ringing',
          incomingCall: call,
          callerNumber: from,
          callDirection: 'inbound',
        }));

        // Resolve parent/customer CallSid already while phone is ringing
        lookupCustomerCallSid(from);

        call.on('cancel', () => {
          setState(s => ({
            ...s,
            status: 'ready',
            incomingCall: null,
            callerNumber: '',
            callDirection: null,
          }));
        });

        call.on('disconnect', () => {
          handleCallEnd();
        });
      });

      device.on('tokenWillExpire', async () => {
        try {
          await supabase.auth.getUser(); // force refresh
          const { data: { session: newSession } } = await supabase.auth.getSession();
          if (!newSession) return;
          const refreshRes = await fetch(
            `https://${projectId}.supabase.co/functions/v1/twilio-token`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${newSession.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({}),
            }
          );
          if (refreshRes.ok) {
            const { token: newToken } = await refreshRes.json();
            device.updateToken(newToken);
          }
        } catch (err) {
          console.error('Token refresh failed, re-initializing device:', err);
          // If token refresh fails, destroy and re-init the whole device
          if (deviceRef.current) {
            deviceRef.current.destroy();
            deviceRef.current = null;
          }
          initCalledRef.current = false;
          initialize();
        }
      });

      await device.register();
      deviceRef.current = device;
    } catch (err) {
      console.error('Softphone init error:', err);
      setState(s => ({ ...s, status: 'offline' }));
      toast.error('Kunne ikke starte telefon');
    }
  }, [authUser]);

  // Look up the customer's original Twilio CallSid from switchboard_calls
  // (browser-leg SID is different from the customer's parent call SID)
  const lookupCustomerCallSid = useCallback(async (callerNumber: string): Promise<string | null> => {
    if (!user?.company_id) return null;
    try {
      // Normalize the caller number to match DB format (+45...)
      let normalized = callerNumber.replace(/\s/g, '');
      if (!normalized.startsWith('+')) {
        normalized = normalized.startsWith('00') ? '+' + normalized.slice(2) : '+45' + normalized;
      }
      const cleanNumber = normalized.replace(/^client:/, '');

      const { data: calls } = await (supabase as any)
        .from('switchboard_calls')
        .select('twilio_call_sid, status, caller_number')
        .eq('company_id', user.company_id)
        .eq('direction', 'inbound')
        .in('status', ['ringing', 'queued', 'in-progress'])
        .not('twilio_call_sid', 'is', null)
        .order('created_at', { ascending: false })
        .limit(5);

      if (calls && calls.length > 0) {
        const match = calls.find((c: any) => c.caller_number === cleanNumber) || calls[0];
        customerCallSidRef.current = match.twilio_call_sid;
        setState(s => ({ ...s, customerCallSid: match.twilio_call_sid }));
        console.log('Customer CallSid found:', match.twilio_call_sid, 'status:', match.status);

        // Re-check after 2 seconds in case status updates
        setTimeout(async () => {
          try {
            const { data: freshCalls } = await (supabase as any)
              .from('switchboard_calls')
              .select('twilio_call_sid, status, caller_number')
              .eq('company_id', user.company_id)
              .eq('direction', 'inbound')
              .in('status', ['in-progress', 'ringing'])
              .not('twilio_call_sid', 'is', null)
              .order('created_at', { ascending: false })
              .limit(3);
            if (freshCalls && freshCalls.length > 0) {
              const freshMatch = freshCalls.find((c: any) => c.caller_number === cleanNumber) || freshCalls[0];
              customerCallSidRef.current = freshMatch.twilio_call_sid;
              setState(s => ({ ...s, customerCallSid: freshMatch.twilio_call_sid }));
              console.log('Customer CallSid refreshed:', freshMatch.twilio_call_sid);
            }
          } catch (_) { /* ignore refresh errors */ }
        }, 2000);

        return match.twilio_call_sid;
      }

      console.warn('No customer CallSid found in switchboard_calls');
      return null;
    } catch (err) {
      console.error('Failed to lookup customer CallSid:', err);
      return null;
    }
  }, [user]);

  // Start duration timer
  const startTimer = useCallback(() => {
    callStartRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - callStartRef.current) / 1000);
      setState(s => ({ ...s, callDuration: elapsed }));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleCallEnd = useCallback(() => {
    setState(s => {
      if (s.status === 'consulting') {
        console.log('handleCallEnd: ignoring because status is consulting (warm transfer active)');
        return s;
      }
      if ((s.status === 'connecting' || pendingWarmConsultRef.current) && s.consultingNumber) {
        console.log('handleCallEnd: ignoring because warm transfer is being set up (status:', s.status, ')');
        return s;
      }

      stopTimer();

      // Restore audio output (in case speaker was muted during the call)
      try {
        document.querySelectorAll('audio').forEach(el => {
          const audio = el as HTMLAudioElement;
          if (audio.muted) {
            audio.muted = false;
            audio.volume = (audio as any).__prevVolume ?? 1;
          }
        });
      } catch (_) { /* ignore */ }

      const lastCallInfo: LastCallInfo = {
        callId: null, // will be resolved via callSid
        customerName: null, // will be resolved via polling
        callerNumber: s.callerNumber,
        duration: s.callDuration,
        direction: s.callDirection,
        ivrSelection: null,
        callSid: customerCallSidRef.current || activeCallSidRef.current,
      };

      // Update switchboard_calls to completed
      const customerSid = customerCallSidRef.current;
      const browserSid = activeCallSidRef.current;
      const now = new Date().toISOString();
      const finalDuration = s.callDuration || 0;

      if (customerSid) {
        (supabase as any)
          .from('switchboard_calls')
          .update({ status: 'completed', ended_at: now, duration_seconds: finalDuration })
          .eq('twilio_call_sid', customerSid)
          .then(() => {});
      }
      if (browserSid && browserSid !== customerSid) {
        (supabase as any)
          .from('switchboard_calls')
          .update({ status: 'completed', ended_at: now, duration_seconds: finalDuration })
          .eq('twilio_call_sid', browserSid)
          .then(() => {});
      }

      // Outbound fallback: if we never captured a SID but we did log a row,
      // update the most recent outbound ringing/in-progress row for this number.
      const outboundNumber = outboundToNumberRef.current;
      const outboundStartedAt = outboundStartedAtRef.current;
      if (s.callDirection === 'outbound' && !customerSid && !browserSid && outboundCallLoggedRef.current && outboundNumber && user?.company_id) {
        const status = finalDuration > 0 ? 'completed' : 'no-answer';
        (supabase as any)
          .from('switchboard_calls')
          .update({ status, ended_at: now, duration_seconds: finalDuration })
          .eq('company_id', user.company_id)
          .eq('callee_number', outboundNumber)
          .in('status', ['ringing', 'in-progress'])
          .gte('created_at', outboundStartedAt || new Date(Date.now() - 30 * 60 * 1000).toISOString())
          .then(() => {});
      }

      // Log to activity_logs so the call shows up in customer/case activity feeds.
      // Fire-and-forget: resolve customer_id from the switchboard_calls row, then insert.
      if (user?.company_id) {
        const sidForLookup = customerSid || browserSid;
        const direction = s.callDirection;
        const callerNum = s.callerNumber;
        const callee = outboundToNumberRef.current;
        const action = direction === 'outbound'
          ? (finalDuration > 0 ? 'call_completed' : 'call_missed_outbound')
          : (finalDuration > 0 ? 'call_completed' : 'call_missed');

        (async () => {
          try {
            let customerId: string | null = null;
            let leadId: string | null = null;
            if (sidForLookup) {
              const { data } = await (supabase as any)
                .from('switchboard_calls')
                .select('customer_id, lead_id')
                .eq('twilio_call_sid', sidForLookup)
                .maybeSingle();
              customerId = data?.customer_id || null;
              leadId = data?.lead_id || null;
            }

            await supabase.from('activity_logs').insert({
              company_id: user.company_id,
              user_id: user.id,
              entity_type: customerId ? 'customer' : 'call',
              entity_id: customerId || sidForLookup || crypto.randomUUID(),
              action,
              metadata: {
                direction,
                duration_seconds: finalDuration,
                caller_number: callerNum,
                callee_number: callee,
                twilio_call_sid: sidForLookup,
                lead_id: leadId,
              },
            });
          } catch (err) {
            console.warn('[handleCallEnd] activity_log insert failed:', err);
          }
        })();
      }

      customerCallSidRef.current = null;
      outboundCallLoggedRef.current = false;
      outboundToNumberRef.current = null;
      outboundStartedAtRef.current = null;

      return {
        ...s,
        status: deviceRef.current ? 'ready' : 'offline',
        activeCall: null,
        incomingCall: null,
        callDuration: 0,
        callerNumber: '',
        callDirection: null,
        isMuted: false,
        isSpeakerMuted: false,
        isOnHold: false,
        customerCallSid: null,
        lastCallInfo,
      };
    });
  }, [stopTimer, user]);

  // Make outbound call
  const makeCall = useCallback(async (toNumber: string) => {
    const device = deviceRef.current;
    if (!device) {
      toast.error('Telefon ikke klar — genindlæs siden');
      return;
    }

    // Check device registration state
    const deviceState = (device as any).state;
    console.log('[makeCall] Device state:', deviceState, '| Calling:', toNumber);
    if (deviceState !== 'registered') {
      toast.error(`Telefon ikke registreret (${deviceState}). Genindlæs siden.`);
      return;
    }

    try {
      // Safari/iOS: Pre-check microphone permission synchronously from user gesture
      // This prevents Safari from blocking getUserMedia when called indirectly by Twilio SDK
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop()); // Release immediately
          console.log('[makeCall] Safari mic pre-check OK');
        } catch (micErr: any) {
          console.error('[makeCall] Safari mic pre-check failed:', micErr);
          if (micErr.name === 'NotAllowedError') {
            toast.error('Mikrofontilladelse nægtet. Gå til Indstillinger → Safari → Mikrofon og tillad adgang.');
          } else if (micErr.name === 'NotFoundError') {
            toast.error('Ingen mikrofon fundet på enheden.');
          } else {
            toast.error(`Mikrofonfejl: ${micErr.message || 'Ukendt fejl'}`);
          }
          handleCallEnd();
          return;
        }
      }

      setState(s => ({ ...s, status: 'connecting', callerNumber: toNumber, callDirection: 'outbound' }));
      callConnectedRef.current = false;
      outboundCallLoggedRef.current = false;
      outboundToNumberRef.current = toNumber;
      outboundStartedAtRef.current = new Date().toISOString();

      // Helper: insert (once) the switchboard_calls row for this outbound call.
      // We delay until SID is known so twilio-call-status webhook can find the row.
      const logOutboundCall = async (status: 'ringing' | 'in-progress', extra: Record<string, any> = {}) => {
        if (outboundCallLoggedRef.current) return;
        if (!user?.company_id) return;
        const sid = activeCallSidRef.current;
        if (!sid) return; // wait until SID is known
        outboundCallLoggedRef.current = true;
        try {
          await (supabase as any).from('switchboard_calls').insert({
            company_id: user.company_id,
            direction: 'outbound',
            callee_number: toNumber,
            caller_number: '',
            status,
            answered_by: user.id,
            twilio_call_sid: sid,
            ...extra,
          });
        } catch (e) {
          console.warn('[makeCall] log outbound failed', e);
          outboundCallLoggedRef.current = false; // allow retry
        }
      };

      // Clear any previous timeout
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }

      console.log('[makeCall] Calling device.connect with To:', toNumber);
      const call = await device.connect({
        params: { To: toNumber },
      });
      console.log('[makeCall] device.connect returned. CallSid:', call.parameters?.CallSid, 'Status:', (call as any).status);

      // Start 30-second timeout — if call never connects, show error
      callTimeoutRef.current = setTimeout(() => {
        if (!callConnectedRef.current) {
          console.error('[makeCall] Timeout: Call did not connect within 30 seconds');
          try { call.disconnect(); } catch (_) { /* ignore */ }
          handleCallEnd();
          toast.error('Opkaldet kunne ikke forbindes inden for 30 sekunder. Tjek nummeret og prøv igen, eller ring fra en anden telefon.');

          // Log a failed row directly (no SID was ever issued)
          if (user?.company_id && !outboundCallLoggedRef.current) {
            outboundCallLoggedRef.current = true;
            (supabase as any).from('switchboard_calls').insert({
              company_id: user.company_id,
              direction: 'outbound',
              callee_number: toNumber,
              caller_number: '',
              status: 'failed',
              answered_by: user.id,
              notes: 'Timeout: ingen forbindelse inden 30s',
              ended_at: new Date().toISOString(),
            }).then(() => console.log('[makeCall] Logged failed (timeout) outbound call'));
          }
        }
      }, 30000);

      // Capture Twilio Call SID for status tracking
      const callSid = call.parameters?.CallSid || null;
      if (callSid) {
        activeCallSidRef.current = callSid;
        // Log immediately if SID is already available at connect time
        logOutboundCall('ringing');
      }

      call.on('accept', () => {
        callConnectedRef.current = true;
        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
        // Capture SID now if it wasn't available initially
        if (!activeCallSidRef.current && call.parameters?.CallSid) {
          activeCallSidRef.current = call.parameters.CallSid;
        }
        console.log('[makeCall] Call accepted. CallSid:', call.parameters?.CallSid);
        setState(s => ({ ...s, status: 'on-call', activeCall: call }));
        startTimer();
        // Log row now that SID is known. If already logged, update it to in-progress.
        if (!outboundCallLoggedRef.current) {
          logOutboundCall('in-progress', { answered_at: new Date().toISOString() });
        } else if (activeCallSidRef.current) {
          (supabase as any).from('switchboard_calls')
            .update({ status: 'in-progress', answered_at: new Date().toISOString() })
            .eq('twilio_call_sid', activeCallSidRef.current)
            .then(() => {});
        }
      });

      call.on('ringing', () => {
        callConnectedRef.current = true;
        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
        // SID typically arrives by/before the ringing event
        if (!activeCallSidRef.current && call.parameters?.CallSid) {
          activeCallSidRef.current = call.parameters.CallSid;
        }
        console.log('[makeCall] Remote side ringing. CallSid:', activeCallSidRef.current);
        logOutboundCall('ringing');
      });

      call.on('disconnect', () => {
        callConnectedRef.current = true;
        if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
        console.log('[makeCall] Call disconnected');
        handleCallEnd();
      });

      call.on('cancel', () => {
        if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
        console.log('[makeCall] Call cancelled');
        handleCallEnd();
      });

      call.on('reject', () => {
        if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
        console.log('[makeCall] Call rejected');
        handleCallEnd();
      });

      call.on('error', (err: any) => {
        if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
        console.error('[makeCall] Call error object:', JSON.stringify(err, null, 2));
        console.error('[makeCall] Call error code:', err?.code, 'message:', err?.message, 'causes:', err?.causes, 'twilioError:', err?.twilioError);
        handleCallEnd();
        const code = err?.code || err?.causes?.[0]?.code || err?.twilioError?.code;
        const errMsg = err?.message || err?.causes?.[0]?.message || err?.twilioError?.message || '';
        if (code === 31005 || code === 31009) {
          toast.error('Opkaldet blev afbrudt pga. ustabil internetforbindelse. Prøv igen.');
        } else if (code === 31002 || code === 31008) {
          toast.error('Nummeret kunne ikke ringes op. Tjek at nummeret er korrekt.');
        } else {
          toast.error(`Opkaldsfejl (${code || 'ukendt'}) — ${errMsg || 'prøv igen eller ring manuelt.'}`);
        }
      });

    } catch (err: any) {
      if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
      console.error('[makeCall] Exception:', err?.message, err?.code, err);
      handleCallEnd();
      toast.error(`Kunne ikke starte opkald: ${err?.message || 'ukendt fejl'}`);
    }
  }, [user, startTimer, handleCallEnd]);

  // Accept incoming call
  const acceptCall = useCallback(() => {
    const call = state.incomingCall;
    if (!call) return;

    const isConsultingFlow = stateRef.current?.status === 'consulting';

    call.accept();
    // Capture browser-leg CallSid
    const legSid = call.parameters?.CallSid || null;
    if (legSid) activeCallSidRef.current = legSid;

    setState(s => ({
      ...s,
      status: isConsultingFlow ? 'consulting' : 'on-call',
      activeCall: call,
      incomingCall: null,
    }));

    if (!isConsultingFlow) {
      startTimer();
      // Look up customer's original CallSid from switchboard_calls
      lookupCustomerCallSid(state.callerNumber);

      // Log inbound call
      if (user?.company_id) {
        (supabase as any).from('switchboard_calls').insert({
          company_id: user.company_id,
          direction: 'inbound',
          caller_number: state.callerNumber,
          status: 'in-progress',
          answered_by: user.id,
          answered_at: new Date().toISOString(),
        });
      }
    }

    // Fallback: capture SID when accept event fires
    call.on('accept', () => {
      if (!activeCallSidRef.current && call.parameters?.CallSid) {
        activeCallSidRef.current = call.parameters.CallSid;
      }
    });
  }, [state.incomingCall, state.callerNumber, user, startTimer, lookupCustomerCallSid]);

  // Reject incoming call
  // NOTE: We only reject the browser leg locally. We do NOT set agent status to 'busy'
  // and do NOT call twilio-transfer-call, because that would kill all child legs and
  // prevent the sequential ring group from continuing to the next member.
  // Twilio's <Dial action=...> callback will naturally fire when this leg ends,
  // and the webhook will route to the next member in the group.
  const rejectCall = useCallback(() => {
    const incoming = state.incomingCall;
    if (!incoming) return;

    incoming.reject();
    setState(s => ({
      ...s,
      status: 'ready',
      incomingCall: null,
      callerNumber: '',
      callDirection: null,
    }));

    // Auto-reset busy status after 60 seconds if agent hasn't changed it manually
    const currentUserId = user?.id;
    const currentCompanyId = user?.company_id;
    if (currentUserId && currentCompanyId) {
      const setAt = Date.now();
      setTimeout(async () => {
        const { data: currentStatus } = await supabase
          .from('agent_statuses')
          .select('status, updated_at')
          .eq('user_id', currentUserId)
          .maybeSingle();

        const updatedAt = currentStatus?.updated_at ? new Date(currentStatus.updated_at).getTime() : 0;
        const wasSetByThisReject = (setAt - updatedAt) < 65_000;

        if (currentStatus?.status === 'busy' && wasSetByThisReject) {
          await supabase.from('agent_statuses').upsert({
            user_id: currentUserId,
            company_id: currentCompanyId,
            status: 'available',
            updated_at: new Date().toISOString(),
          } as any, { onConflict: 'user_id,company_id' });
          console.log('Auto-reset agent status from busy to available after 60s');
        }
      }, 60_000);
    }
  }, [state.incomingCall, user?.id, user?.company_id]);

  // Hang up active call
  const hangUp = useCallback(() => {
    state.activeCall?.disconnect();
    handleCallEnd();
  }, [state.activeCall, handleCallEnd]);

  // Toggle mute (microphone)
  const toggleMute = useCallback(() => {
    if (state.activeCall) {
      const newMuted = !state.isMuted;
      state.activeCall.mute(newMuted);
      setState(s => ({ ...s, isMuted: newMuted }));
    }
  }, [state.activeCall, state.isMuted]);

  // Toggle speaker (incoming audio output)
  // Mutes all <audio> elements created by the Twilio Voice SDK so the
  // remote party's audio is silenced locally without dropping the call.
  const toggleSpeaker = useCallback(() => {
    const newMuted = !state.isSpeakerMuted;
    try {
      // Twilio Voice SDK creates audio elements appended to document.body
      // with a known className. Mute all of them so output is silenced.
      const audios = document.querySelectorAll('audio');
      audios.forEach(el => {
        const audio = el as HTMLAudioElement;
        audio.muted = newMuted;
        if (newMuted) {
          (audio as any).__prevVolume = audio.volume;
          audio.volume = 0;
        } else {
          audio.volume = (audio as any).__prevVolume ?? 1;
        }
      });
    } catch (err) {
      console.warn('toggleSpeaker failed:', err);
    }
    setState(s => ({ ...s, isSpeakerMuted: newMuted }));
  }, [state.isSpeakerMuted]);

  // Send DTMF
  const sendDigit = useCallback((digit: string) => {
    state.activeCall?.sendDigits(digit);
  }, [state.activeCall]);

  // Helper: refresh customer CallSid before transfer
  const refreshCustomerCallSid = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const { data: freshCalls } = await (supabase as any)
        .from('switchboard_calls')
        .select('twilio_call_sid, status, caller_number')
        .eq('company_id', user.company_id)
        .eq('direction', 'inbound')
        .in('status', ['in-progress', 'ringing'])
        .not('twilio_call_sid', 'is', null)
        .order('created_at', { ascending: false })
        .limit(3);
      if (freshCalls && freshCalls.length > 0) {
        const callerNum = state.callerNumber.replace(/^client:/, '');
        const match = freshCalls.find((c: any) => c.caller_number === callerNum) || freshCalls[0];
        customerCallSidRef.current = match.twilio_call_sid;
        console.log('Transfer: refreshed customer CallSid:', match.twilio_call_sid, 'status:', match.status);
      }
    } catch (e) {
      console.warn('Could not refresh customer CallSid:', e);
    }
  }, [user, state.callerNumber]);

  const getStableCustomerCallSid = useCallback(async (): Promise<string | null> => {
    if (state.customerCallSid) return state.customerCallSid;
    if (customerCallSidRef.current) return customerCallSidRef.current;
    await refreshCustomerCallSid();
    return customerCallSidRef.current;
  }, [state.customerCallSid, refreshCustomerCallSid]);

  // Cold transfer: redirect the customer's call leg to the target
  const coldTransfer = useCallback(async (toNumber: string) => {
    const customerSid = await getStableCustomerCallSid();
    const callSid = customerSid || activeCallSidRef.current;
    if (!callSid) {
      toast.error('Ingen aktiv opkalds-ID fundet');
      return;
    }
    try {
      toast.info(`Viderestiller til ${toNumber}...`);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/twilio-transfer-call`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ callSid, toNumber, type: 'cold' }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Cold transfer API error:', data);
        throw new Error(data?.error || 'Transfer failed');
      }
      toast.success('Opkald viderestillet');
      pendingWarmConsultRef.current = false;
      hangUp();
    } catch (err: any) {
      console.error('Cold transfer error:', err);
      const msg = err?.message?.toLowerCase() || '';
      if (msg.includes('not found') || msg.includes('not in progress')) {
        toast.error('Opkaldet er allerede afsluttet — der er ingen at viderestille.');
      } else if (msg.includes('invalid') || msg.includes('number')) {
        toast.error('Nummeret kunne ikke ringes op. Tjek at nummeret er korrekt.');
      } else {
        toast.error('Viderestilling fejlede. Opkaldet kan være afsluttet i mellemtiden.');
      }
    }
  }, [hangUp, getStableCustomerCallSid]);

  // Warm transfer: park customer, create consult conference for agent + target
  const warmTransfer = useCallback(async (toNumber: string) => {
    const normalizedTarget = normalizePhoneForCompare(toNumber);
    const normalizedCaller = normalizePhoneForCompare(state.callerNumber);

    if (normalizedTarget && normalizedCaller && normalizedTarget === normalizedCaller) {
      toast.error('Du kan ikke varm-viderestille til samme nummer som kunden ringer fra. Brug et andet nummer til test.');
      return;
    }

    const customerSid = await getStableCustomerCallSid();
    const callSid = customerSid || activeCallSidRef.current;
    if (!callSid) {
      toast.error('Ingen aktiv opkalds-ID fundet');
      return;
    }
    try {
      toast.info(`Ringer til ${toNumber} for konsultation...`);

      // Enable consult auto-accept BEFORE backend starts dialing,
      // so we don't miss the incoming consult call on timing races.
      pendingWarmConsultRef.current = true;
      setState(s => ({
        ...s,
        status: s.status === 'consulting' ? 'consulting' : 'connecting',
        consultingNumber: toNumber,
      }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const agentId = authUser ? `agent_${authUser.id}` : undefined;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/twilio-transfer-call`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ callSid, toNumber, type: 'warm', agentIdentity: agentId }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Warm transfer API error:', data);
        throw new Error(data?.error || 'Warm transfer failed');
      }

      const stableCustomerSid = customerSid || callSid;
      customerCallSidRef.current = stableCustomerSid;

      setState(s => ({
        ...s,
        status: s.status === 'consulting' ? 'consulting' : 'connecting',
        consultCallSid: data.consultCallSid,
        holdConferenceName: data.holdConferenceName,
        consultConferenceName: data.consultConferenceName,
        agentConsultCallSid: data.agentConsultCallSid,
        conferenceName: data.consultConferenceName,
        consultingNumber: toNumber,
        customerCallSid: stableCustomerSid,
      }));
    } catch (err: any) {
      pendingWarmConsultRef.current = false;
      console.error('Warm transfer error:', err);
      const msg = err?.message?.toLowerCase() || '';
      if (msg.includes('not found') || msg.includes('not in progress')) {
        toast.error('Opkaldet er allerede afsluttet — der er ingen at viderestille.');
      } else if (msg.includes('invalid') || msg.includes('number')) {
        toast.error('Nummeret kunne ikke ringes op. Tjek at nummeret er korrekt.');
      } else {
        toast.error('Konsultation fejlede. Opkaldet kan være afsluttet i mellemtiden.');
      }
    }
  }, [getStableCustomerCallSid, authUser, normalizePhoneForCompare, state.callerNumber]);

  // Complete warm transfer: move customer from hold into consult conference, agent leaves
  const completeWarmTransfer = useCallback(async () => {
    let custCallSid = state.customerCallSid || customerCallSidRef.current;
    if (!custCallSid) {
      await refreshCustomerCallSid();
      custCallSid = customerCallSidRef.current;
    }

    if (!state.consultConferenceName || !custCallSid) {
      toast.error('Mangler konference-data');
      return;
    }
    try {
      pendingWarmConsultRef.current = false;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/twilio-transfer-call`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'complete_warm',
            holdConferenceName: state.holdConferenceName,
            consultConferenceName: state.consultConferenceName,
            callSid: custCallSid,
            consultCallSid: state.consultCallSid,
            agentConsultCallSid: state.agentConsultCallSid,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && data?.code === 'target_not_connected') {
          const targetStatus = data?.targetStatus || 'unknown';
          const msg = targetStatus === 'busy'
            ? 'Modtageren er optaget — du forbindes tilbage til kunden'
            : 'Modtageren svarede ikke — du forbindes tilbage til kunden';
          toast.error(msg);
          // Auto-cancel: reconnect agent to customer
          try { await cancelWarmTransfer(); } catch (_) { /* best effort */ }
          return;
        }
        throw new Error(data?.error || 'Complete transfer failed');
      }
      toast.success('Kunden er nu forbundet med modtageren');
      // Disconnect agent's active browser call if still alive
      state.activeCall?.disconnect();
      // Force clean state reset (bypass consulting guard in handleCallEnd)
      stopTimer();
      activeCallSidRef.current = null;
      customerCallSidRef.current = null;
      setState(s => ({
        ...s,
        status: deviceRef.current ? 'ready' : 'offline',
        activeCall: null,
        incomingCall: null,
        callDuration: 0,
        callerNumber: '',
        callDirection: null,
        isMuted: false,
        isOnHold: false,
        consultCallSid: null,
        conferenceName: null,
        holdConferenceName: null,
        consultConferenceName: null,
        agentConsultCallSid: null,
        consultingNumber: null,
        customerCallSid: null,
      }));
    } catch (err) {
      console.error('Complete warm transfer error:', err);
      toast.error('Overførsel fejlede');
    }
  }, [state.consultConferenceName, state.holdConferenceName, state.consultCallSid, state.agentConsultCallSid, state.customerCallSid, state.activeCall, stopTimer, refreshCustomerCallSid]);

  // Cancel warm transfer: end consult calls, redirect customer back to agent
  const cancelWarmTransfer = useCallback(async () => {
    let custCallSid = state.customerCallSid || customerCallSidRef.current;
    if (!custCallSid) {
      await refreshCustomerCallSid();
      custCallSid = customerCallSidRef.current;
    }

    try {
      pendingWarmConsultRef.current = false;
      // Next incoming call should be the customer return leg; auto-accept it.
      pendingWarmReconnectRef.current = true;
      setTimeout(() => {
        pendingWarmReconnectRef.current = false;
      }, 20000);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const agentId = authUser ? `agent_${authUser.id}` : undefined;
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/twilio-transfer-call`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'cancel_warm',
            consultCallSid: state.consultCallSid,
            agentConsultCallSid: state.agentConsultCallSid,
            callSid: custCallSid,
            holdConferenceName: state.holdConferenceName,
            agentIdentity: agentId,
          }),
        }
      );
      setState(s => ({
        ...s,
        status: 'on-call',
        consultCallSid: null,
        conferenceName: null,
        holdConferenceName: null,
        consultConferenceName: null,
        agentConsultCallSid: null,
        consultingNumber: null,
      }));
      toast.info('Konsultation annulleret');
    } catch (err) {
      pendingWarmReconnectRef.current = false;
      console.error('Cancel warm transfer error:', err);
      toast.error('Kunne ikke annullere');
    }
  }, [state.consultCallSid, state.agentConsultCallSid, state.customerCallSid, state.holdConferenceName, authUser, refreshCustomerCallSid]);

  // Legacy alias
  const transferCall = coldTransfer;

  // Auto-initialize softphone when user is logged in
  useEffect(() => {
    if (authUser && user?.company_id && !deviceRef.current) {
      // Small delay to ensure auth session is fully restored after page refresh
      const timeout = setTimeout(() => {
        if (!deviceRef.current) {
          initialize();
        }
      }, initCalledRef.current ? 0 : 1500);
      initCalledRef.current = true;
      return () => clearTimeout(timeout);
    }
  }, [authUser, user?.company_id, initialize]);

  // Cleanup — remove agent status when softphone unmounts
  useEffect(() => {
    return () => {
      stopTimer();
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      initCalledRef.current = false; // Allow re-init on next mount
      // Remove agent status row so they show as offline
      if (user?.id && user?.company_id) {
        supabase.from('agent_statuses')
          .delete()
          .eq('user_id', user.id)
          .eq('company_id', user.company_id)
          .then(() => {});
      }
    };
  }, [stopTimer, user?.id, user?.company_id]);

  const clearLastCallInfo = useCallback(() => {
    setState(s => ({ ...s, lastCallInfo: null }));
  }, []);

  return {
    ...state,
    initialize,
    makeCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    toggleSpeaker,
    sendDigit,
    transferCall,
    coldTransfer,
    warmTransfer,
    completeWarmTransfer,
    cancelWarmTransfer,
    isInitialized: !!deviceRef.current,
    setPendingDequeue,
    clearLastCallInfo,
  };
}
